import { Component, OnInit } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { Router } from "@angular/router"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatIconModule } from "@angular/material/icon"
import { MatSelectModule } from "@angular/material/select"
import { PageHeaderComponent } from "../../../shared/page-header/page-header.component"
import { DataLoaderComponent } from "../../../shared/data-loader/data-loader.component"
import { AuthService } from "../../../services/auth.service"
import {
  SupportCategory,
  SupportService,
  SupportTicket,
} from "../../../services/support.service"

@Component({
  selector: "app-ticket-inbox",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    PageHeaderComponent,
    DataLoaderComponent,
  ],
  templateUrl: "./ticket-inbox.component.html",
  styleUrl: "./ticket-inbox.component.css",
})
export class TicketInboxComponent implements OnInit {
  tickets: SupportTicket[] = []
  categories: SupportCategory[] = []
  loading = false
  filterStatus = "open"
  filterCategory = ""
  filterStationId = ""
  filterSource = ""
  stationOptions: Array<{ id: number; name: string; call_sign: string }> = []

  sourceOptions = [
    { value: "", label: "All Sources" },
    { value: "advertiser", label: "Advertisers" },
    { value: "station", label: "Station Staff" },
  ]
  totalTickets = 0
  currentPage = 1
  itemsPerPage = 20

  statusOptions = [
    { value: "", label: "All Status" },
    { value: "open", label: "Open" },
    { value: "in_progress", label: "In Progress" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" },
  ]

  constructor(
    private supportService: SupportService,
    private authService: AuthService,
    public router: Router,
  ) {}

  get isAdmin(): boolean {
    return this.authService.getCurrentUserInfo()?.role?.toLowerCase() === "admin"
  }

  get canCreateTicket(): boolean {
    return this.supportService.permissions.can_create && !this.isAdmin
  }

  get canViewAnalytics(): boolean {
    return this.supportService.permissions.can_view_analytics
  }

  get canManageSettings(): boolean {
    return this.supportService.permissions.can_manage_settings
  }

  get inboxSubtitle(): string {
    return this.isAdmin
      ? "Tickets from advertisers, owners, and station staff"
      : "Incoming tickets from advertisers at your stations"
  }

  get openCount(): number {
    return this.tickets.filter((t) => t.status === "open").length
  }

  get inProgressCount(): number {
    return this.tickets.filter((t) => t.status === "in_progress").length
  }

  get resolvedCount(): number {
    return this.tickets.filter((t) => t.status === "resolved").length
  }

  get totalPages(): number {
    return Math.ceil(this.totalTickets / this.itemsPerPage) || 0
  }

  get paginationStart(): number {
    if (this.totalTickets === 0) return 0
    return (this.currentPage - 1) * this.itemsPerPage + 1
  }

  get paginationEnd(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalTickets)
  }

  ngOnInit(): void {
    this.supportService.refreshPermissionsFromRole()
    if (!this.supportService.permissions.can_view_inbox) {
      this.router.navigate(["/help"])
      return
    }
    this.loadCategories()
    this.loadFilterOptions()
    this.loadTickets()
  }

  loadFilterOptions(): void {
    this.supportService.getFilterOptions().subscribe({
      next: (res) => {
        this.mergeStationOptions(res?.stations ?? [])
      },
      error: () => {
        this.mergeStationOptions([])
      },
    })
  }

  private mergeStationOptions(
    incoming: Array<{ id: number; name: string; call_sign: string }>,
  ): void {
    const byId = new Map<number, { id: number; name: string; call_sign: string }>()
    for (const station of [...this.stationOptions, ...incoming]) {
      if (station?.id) byId.set(Number(station.id), station)
    }
    this.stationOptions = Array.from(byId.values()).sort((a, b) =>
      (a.name || "").localeCompare(b.name || ""),
    )
  }

  private addStationsFromTickets(tickets: SupportTicket[]): void {
    const fromTickets = tickets
      .filter((t) => t.station_id)
      .map((t) => ({
        id: Number(t.station_id),
        name: t.station_name || `Station ${t.station_id}`,
        call_sign: t.call_sign || "",
      }))
    this.mergeStationOptions(fromTickets)
  }

  ticketSource(ticket: SupportTicket): "advertiser" | "station" {
    return this.supportService.ticketSource(ticket)
  }

  ticketSourceLabel(ticket: SupportTicket): string {
    return this.supportService.ticketSourceLabel(ticket)
  }

  loadCategories(): void {
    this.supportService.getCategories("filter").subscribe({
      next: (categories) => {
        this.categories = categories ?? []
      },
      error: () => {
        this.categories = []
      },
    })
  }

  loadTickets(): void {
    this.loading = true
    this.supportService
      .listTickets({
        view: this.isAdmin ? undefined : "inbox",
        status: this.filterStatus || undefined,
        category: this.filterCategory || undefined,
        station_id: this.filterStationId || undefined,
        // Filter by source on the server so pagination.total matches the rows shown.
        source:
          this.isAdmin && (this.filterSource === "advertiser" || this.filterSource === "station")
            ? this.filterSource
            : undefined,
        limit: this.itemsPerPage,
        offset: (this.currentPage - 1) * this.itemsPerPage,
      })
      .subscribe({
        next: (res) => {
          const incoming = res?.tickets ?? []
          this.addStationsFromTickets(incoming)
          // view=inbox, station_id and source are all filtered by list_support_tickets in both
          // COUNT and LIMIT, so the page and pagination.total are exact — no client re-filtering.
          this.tickets = incoming
          this.totalTickets = res?.pagination?.total ?? incoming.length
          this.loading = false
        },
        error: () => {
          this.tickets = []
          this.totalTickets = 0
          this.loading = false
        },
      })
  }

  onFilterChange(): void {
    this.currentPage = 1
    this.loadTickets()
  }

  clearFilters(): void {
    this.filterStatus = ""
    this.filterCategory = ""
    this.filterStationId = ""
    this.filterSource = ""
    this.currentPage = 1
    this.loadTickets()
  }

  get hasActiveFilters(): boolean {
    return !!(this.filterStatus || this.filterCategory || this.filterStationId || this.filterSource)
  }

  get inboxRows(): Array<
    | { kind: "group"; label: string; key: string }
    | { kind: "ticket"; ticket: SupportTicket }
  > {
    if (!this.isAdmin || this.filterSource) {
      return this.tickets.map((ticket) => ({ kind: "ticket" as const, ticket }))
    }
    const advertisers = this.tickets.filter((t) => this.ticketSource(t) === "advertiser")
    const stationStaff = this.tickets.filter((t) => this.ticketSource(t) === "station")
    const rows: Array<
      | { kind: "group"; label: string; key: string }
      | { kind: "ticket"; ticket: SupportTicket }
    > = []
    if (advertisers.length) {
      rows.push({
        kind: "group",
        key: "advertiser",
        label: `Advertiser tickets (${advertisers.length})`,
      })
      for (const ticket of advertisers) {
        rows.push({ kind: "ticket", ticket })
      }
    }
    if (stationStaff.length) {
      rows.push({
        kind: "group",
        key: "station",
        label: `Station staff tickets to admin (${stationStaff.length})`,
      })
      for (const ticket of stationStaff) {
        rows.push({ kind: "ticket", ticket })
      }
    }
    return rows
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return
    if (page === this.currentPage) return
    this.currentPage = page
    this.loadTickets()
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  getPaginationPages(): number[] {
    const pages: number[] = []
    const maxVisible = 5
    if (this.totalPages <= maxVisible) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i)
      }
    } else {
      const start = Math.max(1, this.currentPage - 2)
      const end = Math.min(this.totalPages, start + maxVisible - 1)
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
    }
    return pages
  }

  openTicket(ticket: SupportTicket): void {
    this.router.navigate(["/help/tickets", ticket.id])
  }

  getCategoryLabel(category: string): string {
    return this.categories.find((cat) => cat.value === category)?.label || category
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      open: "Open",
      in_progress: "In Progress",
      resolved: "Resolved",
      closed: "Closed",
    }
    return labels[status] || status
  }
}
